import type {
  DesktopNotificationBridge,
  DesktopConnection,
  NotificationAttention,
  NotificationResult,
} from '@sharedlib/notification-contract';
import type { NotificationPresenter, NotificationSession, Unsubscribe } from './ports';
import { isOpaqueId, isRecord } from './validation';

export const noopNotificationPresenter: NotificationPresenter = {
  open: async (): Promise<NotificationSession | null> => null,
};

function isBridge(value: unknown): value is DesktopNotificationBridge {
  if (
    !isRecord(value) ||
    value['protocolVersion'] !== 1 ||
    typeof value['connect'] !== 'function' ||
    typeof value['reset'] !== 'function'
  )
    return false;
  const notifications = value['notifications'];
  const windowState = value['windowState'];
  const context = value['context'];
  return (
    isRecord(notifications) &&
    ['show', 'dismiss', 'clearScope', 'ackActivation', 'onActivated'].every(
      (name) => typeof notifications[name] === 'function',
    ) &&
    isRecord(windowState) &&
    typeof windowState['subscribe'] === 'function' &&
    isRecord(context) &&
    typeof context['update'] === 'function'
  );
}

function isAttention(value: unknown): value is NotificationAttention {
  return (
    isRecord(value) &&
    typeof value['revision'] === 'number' &&
    Number.isSafeInteger(value['revision']) &&
    value['revision'] >= 0 &&
    ['focused', 'visible', 'minimized'].every((name) => typeof value[name] === 'boolean') &&
    ['locked', 'unlocked', 'unknown'].includes(String(value['lockState']))
  );
}

function isConnection(value: unknown): value is DesktopConnection {
  return (
    isRecord(value) &&
    value['protocolVersion'] === 1 &&
    isOpaqueId(value['sessionId']) &&
    typeof value['supported'] === 'boolean' &&
    isAttention(value['attention'])
  );
}

function result(value: unknown): NotificationResult {
  if (isRecord(value)) {
    const status = value['status'];
    if (
      status === 'accepted' ||
      status === 'suppressed' ||
      status === 'unsupported' ||
      status === 'invalid' ||
      status === 'stale' ||
      status === 'rate-limited' ||
      status === 'duplicate' ||
      status === 'failed'
    )
      return { status };
  }
  return { status: 'failed' };
}

/** Pass a lazy capability getter; SSR/browser/old-shell paths touch no global window or Notification API. */
export function createDesktopNotificationPresenter(capability: () => unknown): NotificationPresenter {
  return {
    open: async (): Promise<NotificationSession | null> => {
      try {
        const bridge = capability();
        if (!isBridge(bridge)) return null;
        const connection = await bridge.connect();
        if (!isConnection(connection)) return null;
        if (!connection.supported) {
          await bridge.reset(connection.sessionId);
          return null;
        }
        try {
          return connectedSession(bridge, connection);
        } catch {
          await bridge.reset(connection.sessionId);
          return null;
        }
      } catch {
        return null;
      }
    },
  };
}

function connectedSession(bridge: DesktopNotificationBridge, connection: DesktopConnection): NotificationSession {
  const sessionId = connection.sessionId;
  let closed = false;
  let attention = connection.attention;
  const subscriptions = new Set<() => void>();
  const unsubscribeAttention = bridge.windowState.subscribe(sessionId, (event) => {
    if (
      !closed &&
      isRecord(event) &&
      event['sessionId'] === sessionId &&
      isAttention(event['attention']) &&
      event['attention'].revision > attention.revision
    )
      attention = event['attention'];
  });
  subscriptions.add(unsubscribeAttention);
  const ignoreFailure = async (work: () => Promise<unknown>): Promise<void> => {
    if (closed) return;
    try {
      await work();
    } catch {
      /* An unavailable old document must not break the hosted app. */
    }
  };
  return {
    show: async ({ candidate, presentation, contextRevision, suppressIfAttentive }): Promise<NotificationResult> => {
      if (closed) return { status: 'stale' };
      try {
        return result(
          await bridge.notifications.show({
            sessionId,
            id: candidate.id,
            scopeId: candidate.scopeId,
            dedupeId: candidate.dedupeId,
            title: presentation.title,
            body: presentation.body,
            silent: presentation.silent,
            foreground: { suppressIfAttentive, contextRevision, attentionRevision: attention.revision },
          }),
        );
      } catch {
        return { status: 'failed' };
      }
    },
    updateContext: (revision): Promise<void> => ignoreFailure(() => bridge.context.update(sessionId, revision)),
    onActivated: (listener): Unsubscribe => {
      if (closed) return () => undefined;
      let active = true;
      const removeListener = bridge.notifications.onActivated(sessionId, (event) => {
        if (
          active &&
          !closed &&
          isRecord(event) &&
          event['sessionId'] === sessionId &&
          isOpaqueId(event['id']) &&
          isOpaqueId(event['eventId'])
        )
          listener({ id: event['id'], eventId: event['eventId'] });
      });
      const unsubscribe = (): void => {
        active = false;
        removeListener();
      };
      subscriptions.add(unsubscribe);
      return () => {
        if (subscriptions.delete(unsubscribe)) unsubscribe();
      };
    },
    acknowledge: (eventId): Promise<void> =>
      ignoreFailure(() => bridge.notifications.ackActivation(sessionId, eventId)),
    dismiss: (ids): Promise<void> => ignoreFailure(() => bridge.notifications.dismiss(sessionId, ids)),
    clearScope: (scopeId): Promise<void> => ignoreFailure(() => bridge.notifications.clearScope(sessionId, scopeId)),
    close: async (): Promise<void> => {
      if (closed) return;
      closed = true;
      for (const unsubscribe of subscriptions) {
        try {
          unsubscribe();
        } catch {
          /* Continue removing other listeners. */
        }
      }
      subscriptions.clear();
      try {
        await bridge.reset(sessionId);
      } catch {
        /* Main document destruction also invalidates the session. */
      }
    },
  };
}
